import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { authService } from '../api/authService';

const LockContext = createContext(null);
const LOCK_STORAGE_KEY = 'app_is_locked';

export const LockProvider = ({ children }) => {
    // 🟢 التأكد من وجود جلسة دخول حقيقية أولاً (يمكن استخدام sessionStorage أو localStorage حسب نظام التوكن لديك)
    const isAuthenticated = () => {
        const token = sessionStorage.getItem('token') || sessionStorage.getItem('Token') || sessionStorage.getItem('access_token') ||
                      localStorage.getItem('token') || localStorage.getItem('Token') || localStorage.getItem('access_token');
        const email = sessionStorage.getItem('admin_email') || localStorage.getItem('admin_email');
        return Boolean(token && email);
    };

    const [isLocked, setIsLocked] = useState(() => {
        try {
            // 🟢 إذا لم يكن هناك سجل دخول، لا تقفل الشاشة إطلاقاً
            if (!isAuthenticated()) {
                sessionStorage.removeItem(LOCK_STORAGE_KEY);
                return false;
            }
            return sessionStorage.getItem(LOCK_STORAGE_KEY) === 'true';
        } catch (err) {
            console.error('LockContext: failed to read persisted lock state:', err);
            return false;
        }
    });

    const [unlockError, setUnlockError] = useState('');
    const [isUnlocking, setIsUnlocking] = useState(false);

    // 🟢 مراقبة التوكن: لو مسح المستخدم الحساب أو خرج، يتم فك القفل وتصفير الحالة فوراً
    useEffect(() => {
        const checkAuth = () => {
            if (!isAuthenticated() && isLocked) {
                setIsLocked(false);
                sessionStorage.removeItem(LOCK_STORAGE_KEY);
            }
        };

        checkAuth();
        window.addEventListener('storage', checkAuth);
        return () => window.removeEventListener('storage', checkAuth);
    }, [isLocked]);

    // 🔒 قفل الشاشة
    const lock = useCallback(() => {
        // 🟢 منع القفل إذا لم يكن المستخدم مسجلاً لدخوله بالأساس
        if (!isAuthenticated()) {
            setIsLocked(false);
            sessionStorage.removeItem(LOCK_STORAGE_KEY);
            return;
        }

        setUnlockError('');
        setIsLocked(true);
        try {
            sessionStorage.setItem(LOCK_STORAGE_KEY, 'true');
        } catch (err) {
            console.error('LockContext: failed to persist lock state:', err);
        }
    }, []);

    // 🔓 فتح القفل
    const unlock = useCallback(async (password) => {
        const email = sessionStorage.getItem('admin_email') || localStorage.getItem('admin_email');
        if (!email) {
            setUnlockError('لا يمكن التحقق: البريد الإلكتروني غير موجود. الرجاء تسجيل الدخول من جديد.');
            // 🟢 تنظيف حالة القفل فوراً
            setIsLocked(false);
            sessionStorage.removeItem(LOCK_STORAGE_KEY);
            return false;
        }

        setIsUnlocking(true);
        setUnlockError('');
        try {
            await authService.login(email, password);
            setIsLocked(false);
            try {
                sessionStorage.removeItem(LOCK_STORAGE_KEY);
            } catch (err) {
                console.error('LockContext: failed to clear persisted lock state:', err);
            }
            return true;
        } catch (err) {
            setUnlockError(
                err?.response?.data?.detail || 'كلمة المرور غير صحيحة، حاول مرة أخرى.'
            );
            return false;
        } finally {
            setIsUnlocking(false);
        }
    }, []);

    return (
        <LockContext.Provider value={{ isLocked, lock, unlock, unlockError, isUnlocking }}>
            {children}
        </LockContext.Provider>
    );
};

export const useLock = () => {
    const ctx = useContext(LockContext);
    if (!ctx) {
        throw new Error('useLock يجب أن يُستخدم داخل LockProvider');
    }
    return ctx;
};